import React, { createContext, FC, useState } from 'react'

import {
	Box,
	Button,
	Popover,
	Typography,
	IconButton,
} from '@mui/material'
import { useAlert } from "react-alert"
import MoreVertIcon from '@mui/icons-material/MoreVert'

import AddNewModuleModal from './AddNewModule/AddNewModuleModal'
import theme from '@app/theme'
import {
	selectUserOrganizationId,
} from '@app/store/reducers/user/selectors'
import { AppIcon } from '@app/components/AppIcon'
import { EduAndDocumentState } from "@app/types/user"
import { useAppDispatch } from "@app/hooks/useAppDispatch"
import { useTypedSelector } from "@app/hooks/useTypedSelector"
import DeleteModuleForm from './DeleteModuleForm/DeleteModuleForm'
import { selectOrgSettingsOrganizationEducationalContent } from "@app/store/reducers/orgSettings/organization/selectors";
import { orgSettingUpdateOrganizationDetails } from "@app/store/reducers/orgSettings/organization/actions";

const buttonSx = {
	justifyContent: 'flex-start',
	width: '100%',
	'& path': { fill: `${theme.palette.primary.main} !important` },
	px: 4,
	fontFamily: 'Gilroy-Medium, sans-serif',
	color: theme.palette.text.primary,
	fontSize: '0.9375rem',
}

interface AdditionalActionsProps {
	status: string,
	contentId: string,
	loading: boolean,
	updateContentStatus: () => void
	setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export interface CreateEducationModuleContext{
	setPopoverAnchor : React.Dispatch<HTMLButtonElement | null> | null
}

export const CreateContextEducationModuleOptions = createContext<CreateEducationModuleContext>({
	setPopoverAnchor: null
})

const AdditionalActions: FC<AdditionalActionsProps> = ({
	status, contentId, updateContentStatus, loading,
}: AdditionalActionsProps) => {
	const alert = useAlert()
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(
		null
	)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const dispatch = useAppDispatch()
	const eduContent = useTypedSelector(
			selectOrgSettingsOrganizationEducationalContent
	)
	const userOrgId = useTypedSelector(selectUserOrganizationId)
	const isActive = status.toUpperCase() === EduAndDocumentState.ACTIVE;

	const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
		event.stopPropagation()
		setAnchorEl(event.currentTarget)
	}

	const handleClose = (): void => {
		setAnchorEl(null)
	}

	const handleDeleteContent = async (): Promise<void> => {
		setIsLoading(true)
		const updatedContent = eduContent.filter(item => item._id !== contentId)
		try {
			await dispatch(orgSettingUpdateOrganizationDetails({
				_id: userOrgId,
				educationalContent: updatedContent
			}))

			alert.success(`Module deleted`)
		} catch (e: any) {
			'message' in e && alert.error(e.message)
		} finally {
			setIsLoading(false)
		}
	}

	const handleChangeContentStatus = (): void => {
		updateContentStatus();
		handleClose()
	}

	const open = !!anchorEl
	const popoverId = open
		? 'patient-dashboard-call-actions-popover'
		: undefined

	return (
		<CreateContextEducationModuleOptions.Provider
			value={{ setPopoverAnchor: setAnchorEl }}
		>
			<IconButton
				aria-describedby={popoverId}
				onClick={handleOpen}
				aria-label={'Open additional options'}
				disableFocusRipple
				disabled={loading}
				sx={{
					padding: 1,
					ml: 4,
					borderRadius: '0.625rem',
					width: '2.75rem',
					height: '2.75rem',
					bgcolor: open
						? theme.palette.secondary.main
						: 'transparent',
				}}
			>
				<MoreVertIcon />
			</IconButton>

			<Popover
				id={popoverId}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				sx={{
					mt: 2.5,
					'& .MuiPopover-paper': {
						boxShadow: '1px 2px 6px 0 rgba(0, 0, 0, 0.16)',
					},
				}}
				onClick={e => e.stopPropagation()}
			>
				<Box
					py={2}
					px={1}
					display={'flex'}
					flexDirection={'column'}
					alignItems={'flex-start'}
					width={'14.1rem'}
				>
					<AddNewModuleModal
						action={'Edit'}
						_id={contentId}
						closePopover={handleClose}
						disabled={!isActive}
					/>

					<DeleteModuleForm
						isLoading={isLoading}
						handleDeleteContent={handleDeleteContent}
					/>

					<Button
						startIcon={
							<AppIcon
								type={isActive ? 'deactivate' : 'activate'}
							/>
						}
						size={'large'}
						sx={{
							...buttonSx,
							'& .MuiButton-startIcon': {
								mr: 2.7,
							},
						}}
						aria-label={'Send email'}
						onClick={handleChangeContentStatus}
					>
						<Typography
							fontFamily={'Gilroy-Medium'}
							fontSize={'0.9375rem'}
						>
							{isActive
								? 'Deactivate'
								: 'Activate'}
						</Typography>
					</Button>
				</Box>
			</Popover>
		</CreateContextEducationModuleOptions.Provider>
	)
}

export default AdditionalActions
